import React, { useMemo } from 'react';
import { cn } from '../../lib/utils';
import { orderPermGroups, orderWidgetGroups, countWidgetChecks } from './situationwidget/matrixGroups';

// 2026-06-17 사이드바 그룹 구조로 컬럼 정렬 — 칸·줄 묶기 계산은 situationwidget/matrixGroups.js(2026-10-24 분리).
//   카운트는 라벨 위에 가로로 — 라벨이 길어 잘려도 카운트는 항상 보임.
// 2026-10-24 테넌트별 위젯 연결: 테넌트 화면에서 기본 틀과 다르게 고친 칸은 '바꿈' + [기본으로](그 칸을 기본으로 되돌림).

const NAME_COL_WIDTH = 180;
const PERM_COL_WIDTH = 70;

export default function SituationWidgetMatrix({
  widgets,
  permissions,
  connectionMap,
  onToggleCell,
  pendingPermCodes,
  companies = [],
  hqCode = null,
  nameOf = null,
  mode = 'global',
  overridden = null,
  onResetPerm = null,
}) {
  // 권한 그룹: 사이드바 그룹 순서 + 회사(보고 있는 테넌트) + 통합 메뉴 + 마지막에 미분류
  const orderedPermGroups = useMemo(
    () => orderPermGroups(permissions, { companies, mode, nameOf }),
    [permissions, companies, mode, nameOf],
  );

  const orderedWidgetGroups = useMemo(
    () => orderWidgetGroups(widgets, { companies, hqCode, nameOf }),
    [widgets, companies, hqCode, nameOf],
  );

  // 위젯별 체크된 권한 갯수
  const widgetCheckCount = useMemo(() => countWidgetChecks(permissions, connectionMap), [permissions, connectionMap]);

  return (
    <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] overflow-hidden min-w-0 w-full">
      <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between gap-3">
        <div>
          <h3 className="m-0 text-base font-black text-[var(--text)]">매트릭스 view</h3>
          <p className="mt-1 text-xs text-[var(--text-sub)]">
            행=위젯, 열=업무/기능 (사이드바 메뉴 순서). 카운트=연결된 갯수. 체크박스 클릭 시 자동 저장.
          </p>
        </div>
        {pendingPermCodes.size > 0 && (
          <span className="text-xs font-bold text-indigo-600">
            저장 중... ({pendingPermCodes.size}건)
          </span>
        )}
      </div>
      <div className="relative overflow-auto max-h-[calc(100vh-220px)]">
        <table className="border-separate border-spacing-0 text-[11px]">
          <thead>
            {/* 사이드바 그룹 헤더 row */}
            <tr>
              <th
                className="sticky top-0 left-0 z-40 bg-[var(--bg-card)] border-b border-r border-[var(--border)] px-2 py-1 text-left"
                style={{ minWidth: NAME_COL_WIDTH, width: NAME_COL_WIDTH }}
              >
                <span className="text-[10px] font-black text-[var(--text-sub)]">위젯 \ 업무·기능</span>
              </th>
              {orderedPermGroups.map((group) => (
                <th
                  key={`perm-group-${group.key}`}
                  colSpan={group.items.length}
                  className="sticky top-0 z-30 border-b border-r border-[var(--border)] bg-slate-100 text-slate-700 dark:bg-[var(--bg-subtle)] dark:text-[var(--text-sub)] px-2 py-1 text-left text-[10px] font-black"
                >
                  <span className="sticky inline-block" style={{ left: NAME_COL_WIDTH + 4 }}>
                    {group.label} ({group.items.length})
                  </span>
                </th>
              ))}
            </tr>
            {/* 권한 라벨 + 카운트 row (라벨 마지막 줄 아래에 카운트) */}
            <tr>
              <th
                className="sticky top-[28px] left-0 z-40 bg-[var(--bg-card)] border-b border-r border-[var(--border)] px-2 py-1 text-left"
                style={{ minWidth: NAME_COL_WIDTH, width: NAME_COL_WIDTH }}
              >
                <span className="text-[10px] font-black text-[var(--text-sub)]">위젯 이름</span>
              </th>
              {orderedPermGroups.flatMap((group) => (
                group.items.map((perm) => {
                  const count = (connectionMap[perm.code] || []).length;
                  return (
                    <th
                      key={`perm-${perm.code}`}
                      className="sticky top-[28px] z-20 bg-[var(--bg-card)] border-b border-r border-[var(--border)] px-1 py-1 align-bottom"
                      style={{ minWidth: PERM_COL_WIDTH, width: PERM_COL_WIDTH, maxWidth: PERM_COL_WIDTH }}
                    >
                      <div
                        className="flex flex-col text-center"
                        title={`${perm.label} (${perm.code}) — 연결 ${count}건`}
                        style={{ minHeight: 58 }}
                      >
                        <div
                          className="text-[10px] font-bold text-[var(--text)] leading-tight break-keep"
                          style={{ wordBreak: 'keep-all', overflowWrap: 'break-word' }}
                        >
                          {perm.label}
                        </div>
                        <div
                          className={cn(
                            'text-[10px] font-black leading-tight mt-0.5',
                            count > 0 ? 'text-indigo-600' : 'text-[var(--text-muted)]'
                          )}
                        >
                          {count}
                        </div>
                        {overridden && overridden.has(perm.code) && (
                          <div className="mt-0.5 flex flex-col items-center leading-tight">
                            <span className="text-[9px] font-black text-amber-600">바꿈</span>
                            {onResetPerm && (
                              <button
                                type="button"
                                onClick={() => onResetPerm(perm)}
                                disabled={pendingPermCodes.has(perm.code)}
                                className="text-[9px] font-bold text-[var(--text-sub)] underline cursor-pointer disabled:opacity-50"
                              >
                                기본으로
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })
              ))}
            </tr>
          </thead>
          <tbody>
            {orderedWidgetGroups.map((wgroup) => (
              <React.Fragment key={`wgroup-${wgroup.key}`}>
                {/* 위젯 그룹 헤더 row */}
                <tr>
                  <th
                    colSpan={1 + orderedPermGroups.reduce((sum, g) => sum + g.items.length, 0)}
                    className={cn(
                      'border-b border-r border-[var(--border)] px-2 py-1 text-left text-[10px] font-black',
                      wgroup.tone
                    )}
                  >
                    <span className="sticky left-2 inline-block">
                      {wgroup.label} 위젯 ({wgroup.items.length})
                    </span>
                  </th>
                </tr>
                {wgroup.items.map((widget) => {
                  const wCount = widgetCheckCount[widget.code] || 0;
                  return (
                    <tr key={`widget-${widget.code}`} className="hover:bg-[var(--bg-subtle)]">
                      <th
                        className="sticky left-0 z-10 bg-[var(--bg-card)] border-b border-r border-[var(--border)] px-2 py-1 text-left align-middle"
                        style={{ minWidth: NAME_COL_WIDTH, width: NAME_COL_WIDTH }}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="text-[11px] font-bold text-[var(--text)] whitespace-normal break-keep leading-tight">
                            {widget.name}
                          </div>
                          <span className="text-[10px] font-black text-indigo-600 shrink-0">{wCount}</span>
                        </div>
                        <div className="text-[9px] text-[var(--text-muted)] mt-0.5 font-mono truncate" title={widget.code}>
                          {widget.code}
                        </div>
                      </th>
                      {orderedPermGroups.flatMap((group) => (
                        group.items.map((perm) => {
                          const codes = connectionMap[perm.code] || [];
                          const checked = codes.includes(widget.code);
                          const pending = pendingPermCodes.has(perm.code);
                          return (
                            <td
                              key={`cell-${widget.code}-${perm.code}`}
                              className="border-b border-r border-[var(--border)] text-center align-middle p-0"
                              style={{ width: PERM_COL_WIDTH }}
                            >
                              <label className="flex items-center justify-center cursor-pointer py-1">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={pending}
                                  onChange={() => onToggleCell(perm.code, widget.code, !checked)}
                                  className="w-3.5 h-3.5 cursor-pointer accent-indigo-600"
                                />
                              </label>
                            </td>
                          );
                        })
                      ))}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
